import { sortBy } from 'lodash-es'

import { getDefaultRules, getOwnRuleId } from 'components/availability/form/utils'

import { DEFAULT_RULE_ID } from './constants'
import { getAvailabilityFromRule } from './get_availability_from_rule'
import { isUsingCustomHours } from './is_using_custom_hours'
import { isUsingSecondarySchedule } from './is_using_secondary_schedule'


const getAvailabilityRule = (availabilityRule, id) => availabilityRule?.rules.find(({ id: ruleId }) => ruleId === id)

const getActiveAvailabilityRule = (membership, availabilityRules, primaryRule) => {
  if (membership.active_availability_rule_id === membership.own_availability_rule?.id) {
    return membership.own_availability_rule
  }

  if (!availabilityRules && !membership.id) {
    return membership?.owner_availability_rules?.[0]
  }

  return getAvailabilityRule(availabilityRules, membership.active_availability_rule_id) || primaryRule
}

const getSchedules = (membership, membershipRules, primaryRule) => {
  const usingSecondarySchedule = isUsingSecondarySchedule(membership)

  if (usingSecondarySchedule) {
    return membershipRules.rules
  }

  return [primaryRule]
}

const getAvailability = (membership, ownerAvailabilityRules, primaryRule) => {
  const isPending = membership.is_pending
  const activeRule = getActiveAvailabilityRule(membership, ownerAvailabilityRules, primaryRule)
  const usingCustomHours = isUsingCustomHours(membership)
  const usingSecondarySchedule = isUsingSecondarySchedule(membership)
  const { own_availability_rule: ownRule } = membership
  const activeAvailabilityRuleId = usingCustomHours ? 'custom' : activeRule?.id

  const defaultHoursId = isPending ? DEFAULT_RULE_ID : membership.primary_availability_rule_id

  return {
    ...getAvailabilityFromRule(activeRule),
    ownerAvailabilityRules,
    usingCustomHours,
    usingSecondarySchedule,
    activeAvailabilityRuleId,
    ownRule,
    defaultHoursId,
    customRuleId: getOwnRuleId(membership),
    defaultHours: getAvailabilityFromRule(primaryRule),
    schedules: getSchedules(membership, ownerAvailabilityRules, primaryRule),
    primaryRule
  }
}

const getDoubleBookingRules = (rules) => {
  if (!rules) return []

  const sorted = sortBy(rules, ['created_at'])
  return sorted?.map((r) => ({
    id: r.id,
    searchTerm: r.matching_criteria.table.event_name,
    type: r.matching_criteria.table.type,
  }))
}

const getMemberId = (m) => (m.id || (m.is_pending ? `NEW-invitation-${m.organization_invitation_id}` : `NEW-user-${m.user_id}`))

const getMembershipRules = (m, defaultTimezone) => {
  const isPending = m.is_pending
  if (!isPending) {
    return {
      primaryRuleId: m.primary_availability_rule_id,
      rules: m.owner_availability_rules
    }
  }

  const activeAvailabilityRuleId = m.active_availability_rule_id
  const hasCustomRule = activeAvailabilityRuleId && activeAvailabilityRuleId === m.own_availability_rule?.id
  const id = hasCustomRule ? activeAvailabilityRuleId : DEFAULT_RULE_ID

  return {
    primaryRuleId: id,
    rules: [
      {
        id,
        owner_id: id,
        rules: hasCustomRule
          ? m.own_availability_rule?.rules
          : getDefaultRules(),
        timezone: defaultTimezone
      }
    ]
  }
}

export const getMemberData = (m, defaultTimezone) => {
  const isPending = m.is_pending
  const ownerAvailabilityRules = getMembershipRules(m, defaultTimezone)

  const primaryRule = ownerAvailabilityRules.rules.find(
    ({ id }) => id === (ownerAvailabilityRules.primaryRuleId || ownerAvailabilityRules.primary_rule_id)
  )

  return {
    id: m.id,
    memberId: getMemberId(m),
    name: m.name,
    userId: m.user_id,
    userUuid: m.user_uuid,
    organizationInvitationId: isPending ? m.organization_invitation_id : null,
    organizationInvitationUuid: isPending ? m.organization_invitation_uuid : null,
    isPending,
    holidays: m.holidays,
    userHolidaySet: m.user_holiday_set,
    avatar: {
      url: m.avatar_url,
      letter: m.avatar_letter
    },
    doubleBookingRules: getDoubleBookingRules(m.double_booking_rules),
    enabled: m.enabled,
    deleted: m._destroy,
    calendarAccounts: m.calendar_accounts,
    ...getAvailability(m, ownerAvailabilityRules, primaryRule)
  }
}

export const determineMembers = (eventType, defaultTimezone) => {
  if (!eventType) return {}

  const memberships = eventType.memberships || []
  const enabledMemberships = eventType.id ? memberships.filter((m) => m.enabled) : memberships
  if (!enabledMemberships.length) return {}

  return Object.fromEntries(enabledMemberships.map((m) => {
    const memberId = getMemberId(m)
    return [memberId, getMemberData(m, defaultTimezone)]
  }))
}
